import React, { useState, useCallback } from 'react';
import '../../../styles/dev-utils-theme.css';
import { 
  Send, 
  Copy, 
  Download, 
  RefreshCw, 
  Check, 
  AlertCircle,
  Clock,
  Globe,
  Key,
  FileText,
  Plus,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';

interface Header {
  key: string;
  value: string;
  enabled: boolean;
}

interface ApiResponse {
  status: number;
  statusText: string;
  headers: { [key: string]: string };
  data: any;
  time: number;
  size: number;
}

interface RequestHistory {
  id: string;
  method: string;
  url: string;
  timestamp: number;
  status?: number;
  time?: number;
}

function ApiTester() {
  const [url, setUrl] = useState('https://jsonplaceholder.typicode.com/posts/1');
  const [method, setMethod] = useState('GET');
  const [headers, setHeaders] = useState<Header[]>([
    { key: 'Content-Type', value: 'application/json', enabled: true }
  ]);
  const [body, setBody] = useState('');
  const [authType, setAuthType] = useState('none');
  const [authCredentials, setAuthCredentials] = useState({
    bearer: '',
    basic: { username: '', password: '' },
    apiKey: { key: '', value: '', location: 'header' }
  });
  
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState<RequestHistory[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [responseTab, setResponseTab] = useState<'body' | 'headers' | 'raw'>('body');

  const httpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '', enabled: true }]);
  };

  const updateHeader = (index: number, field: keyof Header, value: string | boolean) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setHeaders(newHeaders);
  };

  const removeHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const formatJson = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const getAuthHeaders = () => {
    const authHeaders: { [key: string]: string } = {};
    
    switch (authType) {
      case 'bearer':
        if (authCredentials.bearer) {
          authHeaders['Authorization'] = `Bearer ${authCredentials.bearer}`;
        }
        break;
      case 'basic':
        if (authCredentials.basic.username && authCredentials.basic.password) {
          const credentials = btoa(`${authCredentials.basic.username}:${authCredentials.basic.password}`);
          authHeaders['Authorization'] = `Basic ${credentials}`;
        }
        break;
      case 'apikey':
        if (authCredentials.apiKey.key && authCredentials.apiKey.value) {
          if (authCredentials.apiKey.location === 'header') {
            authHeaders[authCredentials.apiKey.key] = authCredentials.apiKey.value;
          }
        }
        break;
    }
    
    return authHeaders;
  };

  const sendRequest = useCallback(async () => {
    if (!url.trim()) {
      setError('URL is required');
      return;
    }

    setLoading(true);
    setError('');
    const startTime = Date.now();

    try {
      // Prepare headers
      const requestHeaders: { [key: string]: string } = {};
      
      // Add custom headers
      headers.forEach(header => {
        if (header.enabled && header.key && header.value) {
          requestHeaders[header.key] = header.value;
        }
      });

      // Add auth headers
      const authHeaders = getAuthHeaders();
      Object.assign(requestHeaders, authHeaders);

      // Prepare request options
      const requestOptions: RequestInit = {
        method,
        headers: requestHeaders,
      };

      // Add body for non-GET requests
      if (method !== 'GET' && method !== 'HEAD' && body) {
        requestOptions.body = body;
      }

      // Add API key to URL if needed
      let requestUrl = url;
      if (authType === 'apikey' && authCredentials.apiKey.location === 'query' && authCredentials.apiKey.key && authCredentials.apiKey.value) {
        const urlObj = new URL(url);
        urlObj.searchParams.append(authCredentials.apiKey.key, authCredentials.apiKey.value);
        requestUrl = urlObj.toString();
      }

      const response = await fetch(requestUrl, requestOptions);
      const endTime = Date.now();
      const responseTime = endTime - startTime;

      // Parse response
      const contentType = response.headers.get('content-type') || '';
      let responseData;
      
      if (contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      // Get response headers
      const responseHeaders: { [key: string]: string } = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      // Calculate response size
      const responseSize = new Blob([JSON.stringify(responseData)]).size;

      const apiResponse: ApiResponse = {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        data: responseData,
        time: responseTime,
        size: responseSize
      };

      setResponse(apiResponse);

      // Add to history
      const historyItem: RequestHistory = {
        id: Date.now().toString(),
        method,
        url: requestUrl,
        timestamp: Date.now(),
        status: response.status,
        time: responseTime
      };

      setHistory(prev => [historyItem, ...prev.slice(0, 9)]); // Keep last 10 requests

    } catch (err: any) {
      setError(`Request failed: ${err.message}`);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [url, method, headers, body, authType, authCredentials]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadResponse = () => {
    if (!response) return;
    
    const content = typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'api-response.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    setUrl('');
    setMethod('GET');
    setHeaders([{ key: 'Content-Type', value: 'application/json', enabled: true }]);
    setBody('');
    setAuthType('none');
    setAuthCredentials({
      bearer: '',
      basic: { username: '', password: '' },
      apiKey: { key: '', value: '', location: 'header' }
    });
    setResponse(null);
    setError('');
  };

  const loadFromHistory = (historyItem: RequestHistory) => {
    setUrl(historyItem.url);
    setMethod(historyItem.method);
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-600 bg-green-50 border-green-200';
    if (status >= 300 && status < 400) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (status >= 400 && status < 500) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (status >= 500) return 'text-red-600 bg-red-50 border-red-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  return (
    <div className="dev-utils-page" style={{ minHeight: "100vh", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "28px 28px 48px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Globe size={20} style={{ color: "var(--app-primary-mid)" }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", fontFamily: "DM Sans, system-ui, sans-serif" }}>API Tester</span>
        </div>
        <p style={{ fontSize: 13, color: "var(--app-text-2)", margin: 0 }}>Test REST APIs with various HTTP methods, authentication, and detailed response analysis</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top panel - Request Configuration */}
        <div className="lg:col-span-3 space-y-6">
          {/* URL and Method */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex gap-4 mb-4">
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm min-w-[100px]"
              >
                {httpMethods.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter API endpoint URL..."
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              
              <button
                onClick={sendRequest}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
              >
                {loading ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
                {loading ? 'Sending...' : 'Send'}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={clearAll}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                <RefreshCw size={16} />
                Clear All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Authentication */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Key size={20} />
                Authentication
                </h3>
                
                <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Auth Type
                    </label>
                    <select
                    value={authType}
                    onChange={(e) => setAuthType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                    <option value="none">No Authentication</option>
                    <option value="bearer">Bearer Token</option>
                    <option value="basic">Basic Auth</option>
                    <option value="apikey">API Key</option>
                    </select>
                </div>

                {authType === 'bearer' && (
                    <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Bearer Token
                    </label>
                    <input
                        type="text"
                        value={authCredentials.bearer}
                        onChange={(e) => setAuthCredentials(prev => ({ ...prev, bearer: e.target.value }))}
                        placeholder="Enter bearer token..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                    />
                    </div>
                )}

                {authType === 'basic' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Username
                        </label>
                        <input
                        type="text"
                        value={authCredentials.basic.username}
                        onChange={(e) => setAuthCredentials(prev => ({ 
                            ...prev, 
                            basic: { ...prev.basic, username: e.target.value }
                        }))}
                        placeholder="Username"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Password
                        </label>
                        <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={authCredentials.basic.password}
                            onChange={(e) => setAuthCredentials(prev => ({ 
                            ...prev, 
                            basic: { ...prev.basic, password: e.target.value }
                            }))}
                            placeholder="Password"
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        </div>
                    </div>
                    </div>
                )}

                {authType === 'apikey' && (
                    <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Key Name
                        </label>
                        <input
                            type="text"
                            value={authCredentials.apiKey.key}
                            onChange={(e) => setAuthCredentials(prev => ({ 
                            ...prev, 
                            apiKey: { ...prev.apiKey, key: e.target.value }
                            }))}
                            placeholder="API key name (e.g., X-API-Key)"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                        </div>
                        <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Key Value
                        </label>
                        <input
                            type="text"
                            value={authCredentials.apiKey.value}
                            onChange={(e) => setAuthCredentials(prev => ({ 
                            ...prev, 
                            apiKey: { ...prev.apiKey, value: e.target.value }
                            }))}
                            placeholder="API key value"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                        />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Location
                        </label>
                        <select
                        value={authCredentials.apiKey.location}
                        onChange={(e) => setAuthCredentials(prev => ({ 
                            ...prev, 
                            apiKey: { ...prev.apiKey, location: e.target.value as 'header' | 'query' }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                        <option value="header">Header</option>
                        <option value="query">Query Parameter</option>
                        </select>
                    </div>
                    </div>
                )}
                </div>
            </div>

            {/* Headers */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <FileText size={20} />
                    Headers
                </h3>
                <button
                    onClick={addHeader}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                >
                    <Plus size={16} />
                    Add Header
                </button>
                </div>
                
                <div className="space-y-2">
                {headers.map((header, index) => (
                    <div key={index} className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={header.enabled}
                        onChange={(e) => updateHeader(index, 'enabled', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <input
                        type="text"
                        value={header.key}
                        onChange={(e) => updateHeader(index, 'key', e.target.value)}
                        placeholder="Header name"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                    />
                    <input
                        type="text"
                        value={header.value}
                        onChange={(e) => updateHeader(index, 'value', e.target.value)}
                        placeholder="Header value"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                    />
                    <button
                        onClick={() => removeHeader(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                        <Trash2 size={16} />
                    </button>
                    </div>
                ))}
                </div>
            </div>

          </div>

          {/* Request Body */}
          {method !== 'GET' && method !== 'HEAD' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Request Body
              </h3>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter request body (JSON, XML, etc.)..."
                className="w-full h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm dark:bg-gray-700 dark:text-white resize-none"
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => setBody(formatJson(body))}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                >
                  Format JSON
                </button>
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Bottom Panel - History and Response */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-6">
          {/* Error Display */}
          {error && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center gap-3 text-red-700">
                <AlertCircle size={20} />
                <div>
                  <div className="font-semibold">Request Failed</div>
                  <div className="text-sm mt-1">{error}</div>
                </div>
              </div>
            </div>
          )}

          {/* Response */}
          {response && (
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Response
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2))}
                    className={`flex items-center gap-2 px-3 py-1 rounded text-sm transition-colors ${
                      copied 
                        ? 'bg-green-600 text-white' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResponse}
                    className="flex items-center gap-2 px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-colors"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>

              {/* Response Status */}
              <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                  <div className="font-semibold text-gray-900 dark:text-white">Status</div>
                  <div className={`font-mono ${
                    response.status >= 200 && response.status < 300 ? 'text-green-600' :
                    response.status >= 400 ? 'text-red-600' : 'text-orange-600'
                  }`}>
                    {response.status} {response.statusText}
                  </div>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                  <div className="font-semibold text-gray-900 dark:text-white">Time</div>
                  <div className="text-gray-600 dark:text-gray-400">{response.time}ms</div>
                </div>
                <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                  <div className="font-semibold text-gray-900 dark:text-white">Size</div>
                  <div className="text-gray-600 dark:text-gray-400">
                    {(response.size / 1024).toFixed(2)} KB
                  </div>
                </div>
              </div>

              {/* Response Tabs */}
              <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                <nav className="-mb-px flex space-x-8">
                  {['body', 'headers', 'raw'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setResponseTab(tab as any)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                        responseTab === tab
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Response Content */}
              <div className="max-h-96 overflow-auto">
                {responseTab === 'body' && (
                  <pre className="text-sm font-mono bg-gray-50 dark:bg-gray-700 p-4 rounded overflow-x-auto">
                    {typeof response.data === 'string' 
                      ? response.data 
                      : JSON.stringify(response.data, null, 2)
                    }
                  </pre>
                )}
                
                {responseTab === 'headers' && (
                  <div className="space-y-2">
                    {Object.entries(response.headers).map(([key, value]) => (
                      <div key={key} className="flex gap-2 text-sm">
                        <span className="font-mono font-semibold text-gray-700 dark:text-gray-300 min-w-0 flex-shrink-0">
                          {key}:
                        </span>
                        <span className="font-mono text-gray-600 dark:text-gray-400 break-all">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                {responseTab === 'raw' && (
                  <pre className="text-sm font-mono bg-gray-50 dark:bg-gray-700 p-4 rounded overflow-x-auto">
                    {`Status: ${response.status} ${response.statusText}\n\n`}
                    {Object.entries(response.headers).map(([key, value]) => `${key}: ${value}`).join('\n')}
                    {'\n\n'}
                    {typeof response.data === 'string' 
                      ? response.data 
                      : JSON.stringify(response.data, null, 2)
                    }
                  </pre>
                )}
              </div>
            </div>
          )}
          {/* History */}
          {history.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Clock size={20} />
                Recent Requests
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => loadFromHistory(item)}
                    className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs px-2 py-1 rounded font-mono ${
                        item.method === 'GET' ? 'bg-green-100 text-green-800' :
                        item.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                        item.method === 'PUT' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.method}
                      </span>
                      {item.status && (
                        <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {item.url}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {new Date(item.timestamp).toLocaleTimeString()}
                      {item.time && ` • ${item.time}ms`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
  );
}

export default ApiTester;
